import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { Tabs as MuiTabs, Tab as MuiTab } from '@mui/material';
import { withStyles } from '@mui/styles';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import MenuItem from '../MenuItem/MenuItem';
import Menu from '../Menu/Menu';

const tabStyles = {
  root: {
    textTransform: 'unset',

    '&.with-child': {
      display: 'flex',
      flexDirection: 'row',
    },
  },
};

const tabsStyles = {
  root: {
    color: '#374649',
    '& .MuiTab-textColorSecondary:not(.Mui-disabled, .Mui-selected)': {
      color: '#fff',
    },
    '& .MuiTab-textColorPrimary:not(.Mui-disabled, .Mui-selected)': {
      color: '#374649',
    },
    '& .MuiTab-textColorInherit:not(.Mui-disabled, .Mui-selected)': {
      opacity: 1,
    },
    '&.centered .MuiTabs-flexContainer': {
      justifyContent: 'center',
    },
  },
};

const tabMenuStyles = {
  root: {
    ...tabStyles.root,
    '&.MuiTab-labelIcon': {
      minHeight: 'unset',
    },
  },
  wrapper: {
    flexDirection: 'row-reverse',
  },
};

const CustomizedMuiTabs = withStyles(tabsStyles)(MuiTabs);
const CustomizedTab = withStyles(tabStyles)(MuiTab);
const CustomizedTabWithMenu = withStyles(tabMenuStyles)(MuiTab);

export function Tabs({
  value,
  children,
  onChange,
  component = 'div',
  variant = 'standard',
  centered = true,
  indicatorColor = 'primary',
  textColor = 'primary',
  bgColor = 'transparent',
  ...props
}) {
  const tabsActions = useRef();

  useEffect(() => {
    const content = document.getElementById('content');

    if (!content || !tabsActions?.current) return;

    const updateFunc = debounce(() => {
      if (tabsActions && tabsActions.current) {
        tabsActions.current.updateIndicator();
        tabsActions.current.updateScrollButtons();
      }
    }, 10);

    const observer = new ResizeObserver(() => {
      updateFunc();
    });

    observer.observe(content);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <CustomizedMuiTabs
      action={tabsActions}
      style={{
        backgroundColor: bgColor,
      }}
      className={centered ? 'centered' : ''}
      component={component}
      value={value}
      variant={variant}
      centered={centered}
      indicatorColor={indicatorColor}
      textColor={textColor}
      onChange={onChange}
      {...props}
    >
      {children}
    </CustomizedMuiTabs>
  );
}

export function Tab({ items, handleMenuItemClick, upperCase = false, withChild, ...props }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(anchorEl ? null : event.currentTarget);

  const handleChange = (value) => {
    handleMenuItemClick(value);
    setAnchorEl(null);
  };

  if (!items || items.length === 0) {
    return (
      <CustomizedTab
        style={{
          textTransform: upperCase ? 'uppercase' : 'unset',
        }}
        {...props}
      />
    );
  }

  return (
    <>
      <CustomizedTabWithMenu
        {...props}
        icon={<KeyboardArrowDownIcon />}
        onClick={handleClick}
        className={clsx({ 'with-child': withChild })}
      />
      <Menu open={open} anchorEl={anchorEl} placement="bottom-start" onListMouseLeave={handleClick}>
        {items.length > 0
          ? items.map((item) => (
              <MenuItem key={item.label} onClick={() => handleChange(item.value)}>
                {item.label}
              </MenuItem>
            ))
          : null}
      </Menu>
    </>
  );
}

Tabs.propTypes = {
  component: PropTypes.string,
  value: PropTypes.any,
  variant: PropTypes.string,
  children: PropTypes.node,
  onChange: PropTypes.func,
};

Tab.propTypes = {
  label: PropTypes.any,
  value: PropTypes.any,
};
